/**
 * Legacy KundenBilling "Anzeigen von" dropdown — 18 hardcoded categories,
 * lifted verbatim from
 *   ebuero-ui/src/main/java/com/ebuero/billing/admin/KundenBillingFrame.java
 * (nameGruppen + nameFeature arrays).
 *
 * `match` mirrors the legacy SQL predicates:
 * - Index 0 (Gutschriften):   BIL_FEATURE LIKE 'gut%'
 * - Index 3 (Callcenter):     the SQL LIKE filter in `isCallcenterCascadeFeature`
 * - Index 1 (Nutzungsguthaben): separate Guthaben entity — no BILLING rows match
 * - all others: membership in the legacy nameFeature(idx) array
 */

export interface LegacyCategory {
  /** Stable programmatic key (never localized). */
  key: string;
  /** Display label — exact German string used by legacy Java GUI. */
  label: string;
  /** Predicate against a BILLING row's feature name. */
  match: (feature: string) => boolean;
}

/**
 * Maps a backend BillingGroup `name` code (from `/billings/groups`) to the
 * corresponding LegacyCategory key used by the Billing Details Category
 * dropdown — so the Add Billing dialog's Group dropdown shares its labels
 * with the Billing Details Category filter.
 *
 * Codes taken from live `/billings/groups` (e.g. `creditGroup`,
 * `secretarialServiceGroup`) — unknown codes fall back to the backend's own
 * `description` / `name`.
 */
export const BILLING_GROUP_TO_CATEGORY_KEY: Record<string, string> = {
  creditGroup: 'GUTSCHRIFTEN',
  usageCreditGroup: 'NUTZUNGSGUTHABEN',
  secretarialServiceGroup: 'SEKRETARIATSDIENSTE',
  callcenterCallGroup: 'CALLCENTER',
  callcenterCallsGroup: 'CALLCENTER',
  callcenterGroup: 'CALLCENTER',
  mailServiceGroup: 'POSTSERVICE',
  roomServiceGroup: 'RAUMSERVICE',
  nKohlGroup: 'NKOHL',
  dictationServiceGroup: 'DIKTAT',
  musicOnHoldGroup: 'WARTEMUSIK',
  moHGroup: 'WARTEMUSIK',
  logoProductionGroup: 'LOGO',
  specialServiceGroup: 'SONDERDIENSTLEISTUNGEN',
  specialOfferGroup: 'SONDERAKTIONEN',
  setUpFeeGroup: 'EINRICHTUNGSGEBUEHREN',
  returnedMailGroup: 'POSTRUECKLAEUFER',
  additionalFeeGroup: 'ZUSAETZLICHE_GEBUEHREN',
  commissionGroup: 'PROVISION',
  multiphoneServiceGroup: 'MULTIPHONESERVICE',
  officeClubProductGroup: 'OFFICE_CLUB',
};

/** Legacy key for the Callcenter category — used to exclude/disable. */
export const CALLCENTER_CATEGORY_KEY = 'CALLCENTER';

/**
 * Resolve a BillingGroup to the human label shown in the Billing Details
 * Category dropdown. Falls back through:
 *   1. Explicit `name` → legacy category mapping
 *   2. Case-insensitive match of the backend `description`/`name` against
 *      any legacy category label (catches codes we haven't mapped yet)
 *   3. The provided `fallback` (usually the backend description or name)
 */
export function categoryLabelForGroup(
  groupNameCode: string,
  fallback: string,
): string {
  const mappedKey = BILLING_GROUP_TO_CATEGORY_KEY[groupNameCode];
  if (mappedKey) {
    const found = LEGACY_BILLING_CATEGORIES.find((c) => c.key === mappedKey);
    if (found) return found.label;
  }
  // Defensive: match backend label (e.g. "Callcenter Anrufe") against legacy
  // labels with normalisation so surface-text drift doesn't leak German codes.
  const normalized = fallback.toLowerCase();
  const byLabel = LEGACY_BILLING_CATEGORIES.find((c) =>
    normalized.includes(c.label.toLowerCase()),
  );
  if (byLabel) return byLabel.label;
  return fallback;
}

/**
 * The legacy callcenter feature filter, shared by the KV "Callcenter Anrufe" view,
 * the KV cascade delete (`KundenBillingBean.deleteCCBillingByDokument`) and the
 * invoice-api cascade endpoint (EB-4383):
 *
 *   (bil_feature LIKE 'call%' AND NOT LIKE 'call_Forw%' AND NOT LIKE 'callsekret%')
 *   OR bil_feature LIKE 'cc_min%'
 *
 * Mirrors SQL LIKE exactly: `_` matches any single character, case-sensitive (DB2).
 * Decides by feature code, not by BillingGroup — voicebot features such as
 * `callcenter_AI` / `cc_min_AI` have no group but are cascaded.
 *
 * @ticket EB-4384
 */
export function isCallcenterCascadeFeature(feature: string): boolean {
  return (/^call/.test(feature) && !/^call.Forw/.test(feature) && !/^callsekret/.test(feature)) ||
    /^cc.min/.test(feature);
}

/**
 * True if a BillingGroup represents Callcenter Calls (excluded from Add Billing).
 * Matches both the mapped codes (if present) AND any group whose `name` or
 * `description` contains "callcenter" so a new backend code variant still
 * gets filtered out.
 */
export function isCallcenterGroup(
  groupNameCode: string,
  groupDescription?: string | null,
): boolean {
  if (BILLING_GROUP_TO_CATEGORY_KEY[groupNameCode] === CALLCENTER_CATEGORY_KEY) {
    return true;
  }
  const blob = `${groupNameCode} ${groupDescription ?? ''}`.toLowerCase();
  return blob.includes('callcenter');
}

/**
 * Catalog features that are never offered for manual booking in Add Billing,
 * although `/billings/groups` lists them (EB-4502).
 *
 * - `gut_start` — start credit. KV never offered it; it was last booked in 2006.
 *   Start credit is booked as usage credit (EB-3883, `GUTHABEN`) instead.
 *   Existing rows stay visible and deletable in the billing grid.
 */
const NON_BOOKABLE_FEATURES: ReadonlySet<string> = new Set(['gut_start']);

/** True if a catalog feature may be offered in the Add Billing dialog. */
export function isBookableFeature(featureCode: string): boolean {
  return !NON_BOOKABLE_FEATURES.has(featureCode);
}

/**
 * Features bookable for every user account, whether or not they are assigned to it.
 *
 * - `gut_grund` — cancellation credit. KV `KundenBillingFrame.hasFeature()` returns true for
 *   `FEATURE_GUTSCHRIFT_KUENDIGUNG` regardless of the customer's features
 *   (pinguin_repo 55e211d443, `KundenBillingFrame.java:765-767`).
 */
const ALWAYS_BOOKABLE_FEATURES: ReadonlySet<string> = new Set(['gut_grund']);

/**
 * True if the user account may book `featureCode`: it is assigned to the account
 * (`/billings/features`) or always bookable. `assigned === null` (not loaded or failed) → true.
 */
export function isFeatureAvailable(featureCode: string, assigned: ReadonlySet<string> | null): boolean {
  return !assigned || assigned.has(featureCode) || ALWAYS_BOOKABLE_FEATURES.has(featureCode);
}

const startsWithAny =
  (prefixes: string[]) =>
  (f: string) =>
    prefixes.some((p) => f.startsWith(p));

const inSet = (names: string[]) => {
  const set = new Set(names);
  return (f: string) => set.has(f);
};

export const LEGACY_BILLING_CATEGORIES: LegacyCategory[] = [
  {
    key: 'ALL',
    label: 'All',
    match: () => true,
  },
  {
    key: 'GUTSCHRIFTEN',
    label: 'Credits',
    match: (f) => f.startsWith('gut'),
  },
  {
    key: 'NUTZUNGSGUTHABEN',
    // Legacy writes these to a separate Guthaben entity; BILLING never contains them.
    label: 'Usage Credits',
    match: () => false,
  },
  {
    key: 'SEKRETARIATSDIENSTE',
    label: 'Secretarial Services',
    match: (f) =>
      ['callsekret', 'callsekret_1min', 'ab_einrichtung'].includes(f) ||
      // Automated secretarial events (generated email, SMS, etc. — billed at
      // 0,00 € but still originate from the Sekretariatsdienst pipeline).
      f.startsWith('genGes'),
  },
  {
    key: 'CALLCENTER',
    label: 'Callcenter Calls',
    match: (f) => isCallcenterCascadeFeature(f),
  },
  {
    key: 'POSTSERVICE',
    label: 'Post Service',
    match: inSet([
      'brief_empf',
      'paket_empf',
      'paket_empf_zuschlag',
      'porto',
      'paket_lagerung',
      'paket_lagerung_zuschlag',
      'brief_vers',
      'brief_scan',
      'brief_query',
    ]),
  },
  {
    key: 'RAUMSERVICE',
    label: 'Room Service',
    match: inSet([
      'room_DUS',
      'room_light_DUS',
      'storno',
      'storno_day_before_DUS',
      'storno_same_day_DUS',
      'room',
      'room_light',
      'office_service',
      'print_color',
      'beamer',
      'beamer_1day',
      'beamer_4h',
      'beamer_1h',
      'laptop_1day',
      'laptop_4h',
      'laptop_1h',
      'monitor_full_hd',
      'monitor_full_hd_1day',
      'monitor_full_hd_4h',
      'monitor_full_hd_1h',
      'konferenztelefon_1day',
      'konferenztelefon_4h',
      'konferenztelefon_1h',
      'moderationskoffer',
      'botengang',
      'office_material',
      'fax_office',
      'drinks_juice',
      'coffee',
      'coffee_free',
      'drinks_water',
      'drinks',
      'snacks',
      'copy_b/w',
      'copy_service',
      'print_b/w',
      'flex_office_1day',
      'flex_office_4h',
      'flex_office_1h',
      'catering_service',
      'other_service',
      'catering',
      'storno_day_before',
      'storno_same_day',
      'phone_germany',
      'text_german',
      'text_foreign',
      'wlan',
      'additionalChargeOvertime',
    ]),
  },
  {
    key: 'NKOHL',
    label: 'NKohl',
    match: inSet(['nkohl_gk_satz1', 'nkohl_gk_satz0']),
  },
  {
    key: 'DIKTAT',
    label: 'Dictation Service',
    match: inSet(['diktat_overnight', 'diktat_3days', 'diktat_5days']),
  },
  {
    key: 'WARTEMUSIK',
    label: 'Hold Music Production',
    match: inSet(['mohCreateStandard', 'mohCreatePremium']),
  },
  {
    key: 'LOGO',
    label: 'Logo Production',
    match: inSet(['logo_1proposal', 'logo_3proposal', 'logo_5proposal', 'logo_claim', 'logo_extra']),
  },
  {
    key: 'SONDERDIENSTLEISTUNGEN',
    label: 'Special Services',
    match: inSet(['evn', 'form_erst']),
  },
  {
    key: 'SONDERAKTIONEN',
    label: 'Special Promotions',
    match: inSet(['aktion_tel', 'aktion_ga']),
  },
  {
    key: 'EINRICHTUNGSGEBUEHREN',
    label: 'Setup Fees',
    match: inSet(['setup_number', 'setup_firmenschild']),
  },
  {
    key: 'POSTRUECKLAEUFER',
    label: 'Returned Mail',
    match: inSet(['adress_erm5', 'adress_erm10', 'adress_erm22']),
  },
  {
    key: 'ZUSAETZLICHE_GEBUEHREN',
    label: 'Additional Fees',
    match: inSet([
      'fees_subsequent',
      'fee_CompanyRegistry',
      'fees_additional',
      'packageUpgrade',
    ]),
  },
  {
    key: 'PROVISION',
    label: 'Commission',
    match: inSet(['provisionKvm']),
  },
  {
    key: 'MULTIPHONESERVICE',
    label: 'Multiphoneservice',
    match: inSet(['training_fee', 'object_mapping', 'backoffice']),
  },
  {
    key: 'OFFICE_CLUB',
    label: 'Office Club Products',
    match: startsWithAny([
      'sekret',
      'brief_scan',
      'tea',
      'bitebox',
      'charitea',
      'annahme_von_einschreiben',
      'locker',
      'telefon',
      'firmenschild',
      'paket_handling',
      'catering',
      'kurier',
      'post_vers',
      'room_1h',
      'scan',
      'fritz_kola',
      'club_mate',
      'saft_bio',
      'bier_div',
      'sekt',
      'illy_',
      'trinkschokolade_div',
      'extra_day',
      'copy_A3_b/w',
      'green_card',
      'postw_taglich',
      'print_color_A3',
      'duplo',
      'kinderriegel',
      'balisto',
      'kuchen_div',
      'getranke',
      'whiteboard',
      'funkmikrofone',
      'rednerpult',
      'gebackteller',
      'coffee_1l',
      'tea_1l',
      'lemonaid',
      'twixbistro',
      'trial_day',
      'monitor_hosting',
      'cowo_club_247_access',
      'internet_lan',
      'briefumschlag',
      'einrichtungsgebuehr',
      'wasa_einzeln',
      'sms_versand',
      'letter_',
      'local_phonenumber',
      '0800_phonenumber',
      '0180-5_phonenumber',
      'forwarding_',
      'accepted_call_',
      'postal_invoice',
      'event',
      'knoppers',
      'kitkat',
      'ausgleichfehlbestand',
      'getraenke_basic',
      'meeting_manuell',
      'personal',
      'barzahlung_rng',
      'parkplatz',
      'handling_brief',
      'flex_member',
      'freedrink_tariff_membership',
      'freemonth_user_vacation',
      'desk',
      'infomail_absender',
      'flat_white',
      'fruits',
      'mmsklein',
      'maske',
      'zusatz_empfaenger',
      'aufwand',
      'nespresso',
      'huoberbrezeln',
      'specialdrink',
      'pausensnack',
      'fruehstueckspauschale',
      'verfplegungspauschale',
      'cremant',
      'moebel',
      'telephone_forwarding',
      'zusaetzlicher_arbeitsplatz',
      'postal_handling',
      'postal_collection',
      'video_konferenz',
      'moebel_verkauf',
      'telefonbuch_eintrag',
      'cleaning',
    ]),
  },
];

/**
 * Categories offered in the Billing Details "Category" filter. "Usage Credits" is
 * left out: usage credits live in a separate entity (KV `loadGuthaben`), BILLING
 * never contains them, so the filter could only ever show an empty table. The
 * entry stays in LEGACY_BILLING_CATEGORIES for the Add Billing group labels.
 *
 * @ticket EB-4384
 */
export const FILTERABLE_BILLING_CATEGORIES: readonly LegacyCategory[] = LEGACY_BILLING_CATEGORIES.filter(
  (c) => c.key !== 'NUTZUNGSGUTHABEN',
);
